import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, request } from '../lib/api-client.ts';
import { DashboardLayout } from '../components/DashboardLayout.tsx';
import { CredentialRow } from '../components/settings/CredentialRow.tsx';
import type { AuthUser } from '../types/auth.ts';
import type { CredentialsResponse } from '../types/credential.ts';

type Props = {
  user: AuthUser;
  onLogout: () => void;
};

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; data: CredentialsResponse }
  | { kind: 'error'; message: string };

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';

/**
 * 設定画面（admin専用）。
 *
 * 外部API キー一覧を取得し、各 credential を CredentialRow で編集できる。
 * JWT 系シークレットは Worker 起動時に必要なため、本画面では扱わない（CLI で wrangler secret put）。
 */
export const SettingsPage = ({ user, onLogout }: Props): React.JSX.Element => {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [refreshKey, setRefreshKey] = useState(0);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();

    request<CredentialsResponse>('/api/admin/credentials', {
      signal: controller.signal,
    })
      .then((data) => {
        if (!mountedRef.current) return;
        setStatus({ kind: 'ready', data });
      })
      .catch((err) => {
        if (!mountedRef.current || isAbortError(err)) return;
        const message = err instanceof ApiError ? err.message : '取得に失敗しました';
        setStatus({ kind: 'error', message });
      });

    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, [refreshKey]);

  const refresh = (): void => setRefreshKey((k) => k + 1);

  return (
    <DashboardLayout user={user} onLogout={onLogout}>
      <section style={sectionStyle}>
        <h2 style={headStyle}>外部API キー</h2>
        <p style={noteStyle}>
          外部サービスのキーは AES-GCM で暗号化して保存されます。値は管理画面ではマスク表示されます。
          JWT_SECRET / TRENDS_INGEST_TOKEN / CREDENTIAL_ENCRYPTION_KEY は Worker 起動時に必要なため、
          引き続き <code>wrangler secret put</code> で管理してください。
        </p>
        {renderContent(status, refresh)}
      </section>
    </DashboardLayout>
  );
};

const renderContent = (status: Status, refresh: () => void): React.JSX.Element => {
  if (status.kind === 'loading') return <p style={mutedStyle}>読み込み中…</p>;
  if (status.kind === 'error') return <p style={errorStyle}>{status.message}</p>;
  if (status.data.credentials.length === 0) {
    return <p style={mutedStyle}>登録されている credential がありません。</p>;
  }
  return (
    <div>
      {status.data.credentials.map((rec) => (
        <CredentialRow key={rec.key} record={rec} onUpdated={refresh} />
      ))}
    </div>
  );
};

const sectionStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  background: '#fff',
};
const headStyle: React.CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  fontSize: 16,
  borderBottom: '1px solid #eee',
  background: '#f7f7f7',
};
const noteStyle: React.CSSProperties = {
  color: '#666',
  fontSize: 12,
  margin: 0,
  padding: '12px 16px',
  background: '#f9fafb',
  borderBottom: '1px solid #f0f0f0',
};
const mutedStyle: React.CSSProperties = { color: '#888', fontSize: 13, padding: '12px 16px' };
const errorStyle: React.CSSProperties = { color: '#c0392b', fontSize: 13, padding: '12px 16px' };
